import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useSettings } from '@/store/user'

import DashboardPage from "@/pages/dashboard-page.vue"

import ProfilePage from "@/pages/profile-page.vue"
import UsersPage from '@/pages/users/index-page.vue'

import RetailSupportPage from "@/pages/retail-support-page.vue"

import StoreLocatorPage from "@/pages/store-locator/index-page.vue"
import addStoreLocatorPage from '@/pages/store-locator/add-store-locator.vue'
import editStoreLocatorPage from '@/pages/store-locator/edit-store-locator.vue'

import AddUserPage from "@/pages/users/add-user.vue"
import EditUserPage from "@/pages/users/edit-user.vue"

import PimPage from '@/pages/pim/index-page.vue'
import ProductsPricingPimPage from '@/pages/pim/products-pricing-page.vue'
import ProductsFilterPimPage from '@/pages/pim/products-filter-page.vue'
import productsContentPage from '@/pages/pim/products-content-page.vue'
import collectionsPage from '@/pages/pim/collections-page.vue'
import productsImagePage from '@/pages/pim/products-image-page.vue'

import TranslationsPage from '@/pages/translations/index-page.vue'
import TranslationsBlogPosts from '@/pages/translations/blog-posts-page.vue'
import TranslationsCollections from '@/pages/translations/collections-page.vue'
import TranslationsProducts from '@/pages/translations/products-page.vue'

import SettingsPage from "@/pages/settings-page.vue"
import AccessDeniedPage from "@/pages/access-denied-page.vue"
import NotFoundPage from "@/pages/not-found-page.vue"

// TypeScript augmentation for route meta
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    maxRoleId?: number
    allowedDepartments?: number[]
  }
}

// Shared meta constants
const META_PUBLIC = { requiresAuth: false }
const META_AUTH = { requiresAuth: true }
const META_STORE = { requiresAuth: true, maxRoleId: 10, allowedDepartments: [1, 2, 3] }
const META_TRANSLATIONS = { requiresAuth: true, maxRoleId: 3, allowedDepartments: [1, 4] }
const META_PIM = { requiresAuth: true, maxRoleId: 2, allowedDepartments: [1] }
const META_USERS = { requiresAuth: true, maxRoleId: 2, allowedDepartments: [1, 2, 3, 4] }
const META_ADMIN = { requiresAuth: true, maxRoleId: 2, allowedDepartments: [1] }

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: DashboardPage,
    meta: META_PUBLIC
  },
  {
    path: '/overview',
    name: 'overview',
    component: DashboardPage,
    props: true,
    meta: META_PUBLIC
  },
  {
    path: '/retail-support-program',
    name: 'retail-support-program',
    component: RetailSupportPage,
    props: true,
    meta: META_STORE
  },
  {
    path: '/store-locator',
    name: 'store-locator',
    component: StoreLocatorPage,
    props: true,
    meta: META_STORE
  },
    {
    path: '/add-store-locator',
    name: 'add-store-locator',
    component: addStoreLocatorPage,
    props: true,
    meta: META_STORE
  },
    {
    path: '/edit-store-locator',
    name: 'edit-store-locator',
    component: editStoreLocatorPage,
    props: true,
    meta: META_STORE
  },
  {
    path: '/translations',
    name: 'translations',
    component: TranslationsPage,
    props: true,
    meta: META_TRANSLATIONS
  },
  {
    path: '/translations/products',
    name: 'translations-products',
    component: TranslationsProducts,
    props: true,
    meta: META_TRANSLATIONS
  },
  {
    path: '/translations/collections',
    name: 'translations-collections',
    component: TranslationsCollections,
    props: true,
    meta: META_TRANSLATIONS
  },
  {
    path: '/translations/blog-posts',
    name: 'translations-blog-posts',
    component: TranslationsBlogPosts,
    props: true,
    meta: META_TRANSLATIONS
  },
  {
    path: '/pim',
    name: 'pim',
    component: PimPage,
    props: true,
    meta: META_PIM
  },
  {
    path: '/pim/products-pricing',
    name: 'pim-products-pricing',
    component: ProductsPricingPimPage,
    props: true,
    meta: META_PIM
  },
  {
    path: '/pim/products-filter',
    name: 'pim-products-filter',
    component: ProductsFilterPimPage,
    props: true,
    meta: META_PIM
  },
  {
    path: '/pim/products-content',
    name: 'pim-products-content',
    component: productsContentPage,
    props: true,
    meta: META_PIM
  },
  {
    path: '/pim/collections',
    name: 'pim-collections',
    component: collectionsPage,
    props: true,
    meta: META_PIM
  },
  {
    path: '/pim/products-image',
    name: 'pim-products-image',
    component: productsImagePage,
    props: true,
    meta: META_PIM
  },
  {
    path: '/users',
    name: 'users',
    component: UsersPage,
    props: true,
    meta: META_USERS
  },
  {
    path: '/add-user',
    name: 'add-user',
    component: AddUserPage,
    props: true,
    meta: META_USERS
  },
  {
    path: '/edit-user',
    name: 'edit-user',
    component: EditUserPage,
    props: true,
    meta: META_USERS
  },
  {
    path: "/profile",
    name: "profile",
    component: ProfilePage,
    props: true,
    meta: META_AUTH
  },
  {
    path: '/settings',
    name: 'settings',
    component: SettingsPage,
    meta: META_ADMIN
  },
  {
    path: '/access-denied',
    name: 'access-denied',
    component: AccessDeniedPage,
    meta: META_AUTH
  },
  {
    path: "/:catchAll(.*)",
    name: "Not Found",
    component: NotFoundPage,
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Navigation guard for RBAC
router.beforeEach((to, from, next) => {
  const user = useSettings()

  // Routes that don't require auth (home/login, 404)
  if (!to.meta.requiresAuth) {
    return next()
  }

  // Not logged in — redirect to home (shows login form)
  if (!user.isAuthenticated) {
    return next({ name: 'home' })
  }

  // Check role: userRoleId must be <= maxRoleId
  const maxRoleId = to.meta.maxRoleId
  if (maxRoleId !== undefined && Number(user.userRoleId) > maxRoleId) {
    return next({ name: 'access-denied' })
  }

  // Check department
  const allowedDepts = to.meta.allowedDepartments
  if (allowedDepts && !allowedDepts.includes(Number(user.userDepartmentId))) {
    return next({ name: 'access-denied' })
  }

  next()
})

export default router
